import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { useToastStore } from '../store/toastStore';
import { scheduleAPI } from '../api/client';
import { track } from '../utils/analytics';
import Layout from '../components/Layout';
import ScheduleSheet from '../components/schedule/ScheduleSheet';
import { COMMERCE_ENABLED } from '../config/features';

const WEEKDAYS = [
  { value: 'monday', label: '월', full: '월요일' },
  { value: 'tuesday', label: '화', full: '화요일' },
  { value: 'wednesday', label: '수', full: '수요일' },
  { value: 'thursday', label: '목', full: '목요일' },
  { value: 'friday', label: '금', full: '금요일' },
];

const DIRECTION_KR = { UP: '등교', DOWN: '하교' };

export default function Schedules() {
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshAccess } = useAuthStore();
  const showToast = useToastStore((s) => s.showToast);
  const queryClient = useQueryClient();
  const userId = user?.userId;

  const [updatingSchedules, setUpdatingSchedules] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null); // 삭제 확인 모달용

  // 바텀시트 상태
  const [sheetState, setSheetState] = useState({
    isOpen: false, day: null, direction: null, editSchedule: null,
  });

  // 구독/접근 상태 최신화
  useEffect(() => {
    if (user?.userId) refreshAccess();
  }, [user?.userId]);

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }
  if (!user) return null;

  const { data: schedulesData, isLoading, error: queryError } = useQuery({
    queryKey: ['schedules', userId],
    queryFn: async () => {
      const result = await scheduleAPI.list(userId);
      if (!result.success) throw new Error(result.error || '스케줄을 불러올 수 없습니다');
      return result.data || [];
    },
    enabled: !!userId,
  });

  const schedules = schedulesData ?? [];
  const error = queryError ? (queryError.message || '스케줄을 불러올 수 없습니다') : null;

  // 요일 + 방향별로 스케줄 그룹핑
  const scheduleMap = useMemo(() => {
    const map = {};
    WEEKDAYS.forEach(d => {
      map[d.value] = { UP: null, DOWN: null };
    });
    schedules.forEach(s => {
      if (map[s.day_of_week]) {
        map[s.day_of_week][s.direction] = s;
      }
    });
    return map;
  }, [schedules]);

  const deleteMutation = useMutation({
    mutationFn: async ({ schedule }) => {
      const result = await scheduleAPI.delete(schedule.id);
      if (!result.success) throw new Error(result.error || '알 수 없는 오류');
      return result;
    },
    onMutate: async ({ schedule }) => {
      await queryClient.cancelQueries({ queryKey: ['schedules', userId] });
      const previous = queryClient.getQueryData(['schedules', userId]);
      queryClient.setQueryData(['schedules', userId], (old) => old?.filter((s) => s.id !== schedule.id));
      return { previous };
    },
    onSuccess: (_, { schedule }) => {
      track('schedule-deleted', { direction: schedule.direction, day: schedule.day_of_week });
    },
    onError: (err, { schedule }, context) => {
      if (context?.previous) queryClient.setQueryData(['schedules', userId], context.previous);
      showToast('스케줄 삭제 실패: ' + err.message, 'error');
    },
    onSettled: (_, __, { schedule }) => {
      setUpdatingSchedules(prev => { const next = new Set(prev); next.delete(schedule.id); return next; });
      queryClient.invalidateQueries({ queryKey: ['schedules', userId] });
    },
  });

  const handleDelete = (schedule) => {
    if (updatingSchedules.has(schedule.id)) return;
    setDeleteTarget(schedule);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setUpdatingSchedules(prev => new Set(prev).add(deleteTarget.id));
    deleteMutation.mutate({ schedule: deleteTarget });
    setDeleteTarget(null);
  };

  // 가장 최근 스케줄의 선호좌석 (새 등록 시 기본값)
  const lastSeatPreferences = useMemo(() => {
    if (!schedules.length) return [];
    // created_at 기준 가장 최근 스케줄
    const sorted = [...schedules].sort((a, b) =>
      new Date(b.created_at) - new Date(a.created_at)
    );
    return sorted[0]?.seat_preferences || [];
  }, [schedules]);

  const openSheet = (day, direction, editSchedule = null) => {
    if (!hasAccess) {
      showToast('타요패스가 필요합니다', 'error');
      navigate('/pricing');
      return;
    }
    setSheetState({ isOpen: true, day, direction, editSchedule });
  };

  const closeSheet = () => {
    setSheetState({ isOpen: false, day: null, direction: null, editSchedule: null });
  };

  // 슬롯 렌더링 (등교 or 하교)
  const renderSlot = (day, direction) => {
    const schedule = scheduleMap[day]?.[direction];
    const dirLabel = direction === 'UP' ? '등교' : '하교';

    if (!schedule) {
      return (
        <button
          onClick={() => openSheet(day, direction)}
          className="flex flex-col items-center justify-center h-full min-h-[80px] rounded-xl border-2 border-dashed border-gray-200 hover:border-tayo/40 hover:bg-tayo-light/30 transition-all btn-press group"
        >
          <svg className="w-5 h-5 text-gray-300 group-hover:text-tayo/50 transition-colors mb-1" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          <span className="text-[10px] text-gray-400 group-hover:text-tayo/60 font-medium">{dirLabel} 추가</span>
        </button>
      );
    }

    return (
      <div className="relative rounded-xl p-3 h-full min-h-[80px] transition-all bg-white border border-gray-100">
        {/* 방향 뱃지 */}
        <div className="flex items-center mb-1.5">
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
            direction === 'UP' ? 'text-tayo bg-tayo-light' : 'text-orange-600 bg-orange-50'
          }`}>
            {dirLabel}
          </span>
        </div>

        {/* 정보 */}
        <p className="text-sm font-semibold text-charcoal leading-tight">{schedule.route_name}</p>
        <p className="text-xs text-sub-text mt-0.5">{schedule.bus_time} · {schedule.stop_name}</p>

        {/* 액션 버튼 */}
        <div className="flex gap-1.5 mt-2">
          <button
            onClick={() => openSheet(day, direction, schedule)}
            className="flex-1 text-[10px] font-medium text-tayo bg-tayo-light/60 rounded-lg py-1.5 hover:bg-tayo-light btn-press text-center"
          >
            수정
          </button>
          <button
            onClick={() => handleDelete(schedule)}
            className="flex-1 text-[10px] font-medium text-coral bg-coral/10 rounded-lg py-1.5 hover:bg-coral/20 btn-press text-center"
          >
            삭제
          </button>
        </div>
      </div>
    );
  };

  const hasAccess = !COMMERCE_ENABLED || (user?.accessUntil && new Date(user.accessUntil) > new Date());

  return (
    <Layout title="스케줄 관리">
      {/* 미구독 안내 배너 */}
      {!hasAccess && (
        <div className="mb-4 bg-amber-50 border border-amber-200/60 rounded-2xl p-4 animate-fade-up">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-800">타요패스가 필요해요</p>
              <p className="text-xs text-amber-600 mt-0.5">자동 예약 스케줄을 등록하려면 타요패스가 필요합니다</p>
            </div>
            <button onClick={() => navigate('/pricing')} className="btn-press px-3 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-lg flex-shrink-0">
              요금제
            </button>
          </div>
        </div>
      )}

      {/* 헤더 영역 */}
      <div className="mb-5 flex justify-between items-center">
        <p className="text-sub-text text-sm">
          {schedules.length > 0
            ? `${schedules.length}개 등록됨`
            : '스케줄을 등록해보세요'
          }
        </p>
        <button
          onClick={() => navigate('/schedules/bulk')}
          disabled={!hasAccess}
          className={`px-4 py-2.5 rounded-xl font-semibold text-sm btn-press shadow-card ${hasAccess ? 'bg-tayo text-white hover:bg-tayo-dark' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
        >
          일괄 등록
        </button>
      </div>

      {/* 에러 */}
      {error && (
        <div className="mb-4 bg-coral/10 border border-coral/30 rounded-2xl p-4 animate-fade-up">
          <p className="text-sm text-coral font-medium">{error}</p>
        </div>
      )}

      {/* 로딩 */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-tayo"></div>
          <p className="mt-2 text-sub-text">로딩 중...</p>
        </div>
      )}

      {/* 주간 스케줄 뷰 — 항상 표시 */}
      {!isLoading && (
        <div className="space-y-3">
          {WEEKDAYS.map((day, index) => {
            const upSchedule = scheduleMap[day.value]?.UP;
            const downSchedule = scheduleMap[day.value]?.DOWN;
            const hasAny = upSchedule || downSchedule;

            return (
              <div
                key={day.value}
                className={`rounded-2xl overflow-hidden animate-fade-up stagger-${Math.min(index + 1, 5)} ${
                  hasAny ? 'shadow-card' : ''
                }`}
              >
                {/* 요일 헤더 */}
                <div className={`px-4 py-2.5 flex items-center justify-between ${
                  hasAny ? 'bg-white border-b border-border' : 'bg-gray-50/80'
                }`}>
                  <span className="text-sm font-bold text-charcoal">{day.full}</span>
                  {hasAny && (
                    <span className="text-[10px] text-sub-text">
                      {[upSchedule && '등교', downSchedule && '하교'].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </div>

                {/* 등교 / 하교 그리드 */}
                <div className={`grid grid-cols-2 gap-2 p-2 ${hasAny ? 'bg-gray-50/50' : 'bg-gray-50/30'}`}>
                  {renderSlot(day.value, 'UP')}
                  {renderSlot(day.value, 'DOWN')}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-2xl p-6 mx-6 max-w-sm w-full shadow-xl animate-fade-up">
            <h3 className="text-lg font-bold text-charcoal mb-2">스케줄을 삭제하시겠어요?</h3>
            <p className="text-sm text-sub-text mb-5">
              삭제하면 해당 시간의 자동예약이 중단됩니다.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-3 rounded-xl font-semibold text-sm text-charcoal bg-gray-100 hover:bg-gray-200 transition-colors btn-press"
              >
                취소
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 py-3 rounded-xl font-semibold text-sm text-white bg-coral hover:bg-coral/90 transition-colors btn-press"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 스케줄 등록/수정 바텀시트 */}
      <ScheduleSheet
        isOpen={sheetState.isOpen}
        onClose={closeSheet}
        day={sheetState.day}
        direction={sheetState.direction}
        editSchedule={sheetState.editSchedule}
        defaultSeatPreferences={lastSeatPreferences}
        userId={userId}
      />
    </Layout>
  );
}
